/**
 * Web source cards: results from Partnr's web_search, streamed by partnr-chat
 * as `partnr_sources` SSE events and shown under the reply.
 */
import { supabase } from '@/integrations/supabase/client';

export interface PartnrWebSource {
  url: string;
  title: string;
  site: string;
  snippet: string;
}

/** Most cards one reply shows; more is noise in a 340px panel. */
export const MAX_REPLY_SOURCES = 8;

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/** Validate a `partnr_sources` payload. Drops anything that isn't an http(s) result. */
export function parseWebSources(value: unknown): PartnrWebSource[] {
  const results = (value as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) return [];
  return results.flatMap((r) => {
    const v = r as Record<string, unknown> | null;
    if (!v || !isHttpUrl(v.url)) return [];
    const str = (x: unknown, max: number) => (typeof x === 'string' ? x.substring(0, max) : '');
    return [{
      url: v.url,
      title: str(v.title, 200) || new URL(v.url).hostname,
      site: str(v.site, 100) || new URL(v.url).hostname.replace(/^www\./, ''),
      snippet: str(v.snippet, 300),
    }];
  });
}

/** Append new results, skipping URLs already shown, up to MAX_REPLY_SOURCES. */
export function mergeWebSources(existing: PartnrWebSource[], incoming: PartnrWebSource[]): PartnrWebSource[] {
  const seen = new Set(existing.map((s) => s.url));
  const merged = [...existing];
  for (const s of incoming) {
    if (merged.length >= MAX_REPLY_SOURCES) break;
    if (seen.has(s.url)) continue;
    seen.add(s.url);
    merged.push(s);
  }
  return merged;
}

export interface SourcePreview {
  image: string | null;
  favicon: string | null;
}

const previewCache = new Map<string, Promise<SourcePreview>>();

/**
 * Favicon and OG image through the same edge function a pasted link uses.
 * One request per URL per session; failures resolve to an empty preview.
 */
export function fetchSourcePreview(url: string): Promise<SourcePreview> {
  const cached = previewCache.get(url);
  if (cached) return cached;
  const request = supabase.functions
    .invoke('fetch-link-metadata', { body: { url } })
    .then(({ data, error }) => {
      if (error || !data) return { image: null, favicon: null };
      return {
        image: typeof data.image === 'string' && data.image ? data.image : null,
        favicon: typeof data.favicon === 'string' && data.favicon ? data.favicon : null,
      };
    })
    .catch(() => ({ image: null, favicon: null }));
  previewCache.set(url, request);
  return request;
}

/** Test hook. */
export function clearSourcePreviewCache(): void {
  previewCache.clear();
}
